import {Center, Hide, Icon, isTouchable, Show, Spinner, ThemeIconKey} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {MouseEventHandler} from 'react';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

export interface IconButtonProps extends RequiredTestIdProps {
  icon: ThemeIconKey;
  isDisabled?: boolean;
  isLoading?: boolean;
  tabIndex?: number;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

const iconButtonVariants = cva(
  'flex h-full shrink-0 select-none items-center justify-center gap-2 overflow-hidden border-none p-2 text-[var(--color-white)]',
  {
    variants: {
      state: {
        disabled: 'pointer-events-none cursor-not-allowed opacity-20',
        loading: 'pointer-events-none cursor-not-allowed opacity-50',
        default: '[pointer-events:all] cursor-pointer opacity-60',
      },
      isHoverEnabled: {
        true: 'hover:opacity-100',
        false: '',
      },
    },
  }
);

export function IconButton(props: IconButtonProps) {
  const isTouchableDevice = isTouchable();
  const isDisabledOrLoading = props.isDisabled || props.isLoading;
  const state = match({isDisabled: props.isDisabled, isLoading: props.isLoading})
    .with({isDisabled: true}, () => 'disabled' as const)
    .with({isLoading: true}, () => 'loading' as const)
    .otherwise(() => 'default' as const);

  return (
    <button
      data-testid={suffixTestId('iconButton', props)}
      onClick={props.onClick}
      disabled={isDisabledOrLoading}
      type="button"
      tabIndex={props.tabIndex}
      className={cn(iconButtonVariants({state, isHoverEnabled: !isTouchableDevice}))}
    >
      <Show when={props.isLoading}>
        <Center height={5} width={5}>
          <Spinner color="light" size="small" />
        </Center>
      </Show>
      <Hide when={props.isLoading}>
        <Icon value={props.icon} size={5} />
      </Hide>
    </button>
  );
}
